import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PackageSearch, Loader2, Save, RotateCcw, AlertCircle, Search, X, Link2 } from 'lucide-react';

import {
    fetchItemCategoryList,
    fetchItemSuggestions,
    fetchItemForUpdate,
    checkItemCodePendings,
    submitItemCodeUpdate,
    clearItemSuggestions,
    clearItemForUpdate,
    clearSaveResult,
    resetItemCodeUpdation,
    selectItemCategoryList,
    selectItemSuggestions,
    selectItemDetails,
    selectItemHsnList,
    selectItemUnitsList,
    selectItemSaveResult,
    selectItemCategoryListLoading,
    selectItemSuggestionsLoading,
    selectItemForUpdateLoading,
    selectCheckPendingsLoading,
    selectItemSaveLoading,
    selectItemSaveError,
} from '../../slices/purchaseSlice/itemCodeUpdationSlice';
import { showToast } from '../../utilities/toastUtilities';

const SELECTION_TYPES = [
    { value: '1', label: 'Search By Item Code' },
    { value: '2', label: 'Search By Item Name' },
];

const inputCls =
    'w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60';

const Label = ({ children, required }) => (
    <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const ItemCodeUpdation = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;

    const categoryList = useSelector(selectItemCategoryList);
    const suggestions   = useSelector(selectItemSuggestions);
    const details        = useSelector(selectItemDetails);
    const hsnList        = useSelector(selectItemHsnList);
    const unitsList      = useSelector(selectItemUnitsList);
    const saveResult     = useSelector(selectItemSaveResult);
    const saveError      = useSelector(selectItemSaveError);

    const categoryLoading    = useSelector(selectItemCategoryListLoading);
    const suggestionsLoading = useSelector(selectItemSuggestionsLoading);
    const detailsLoading     = useSelector(selectItemForUpdateLoading);
    const checkingPendings   = useSelector(selectCheckPendingsLoading);
    const saveLoading        = useSelector(selectItemSaveLoading);

    // Search bar
    const [category, setCategory] = useState('');
    const [selectionType, setSelectionType] = useState('');
    const [searchText, setSearchText] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const searchBoxRef = useRef(null);

    // Details form
    const [itemName, setItemName] = useState('');
    const [basicPrice, setBasicPrice] = useState('');
    const [hsnId, setHsnId] = useState('');
    const [specification, setSpecification] = useState('');
    const [units, setUnits] = useState('');
    const [remarks, setRemarks] = useState('');
    const [formError, setFormError] = useState('');

    useEffect(() => {
        dispatch(fetchItemCategoryList());
        return () => dispatch(resetItemCodeUpdation());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close the suggestion dropdown on outside click
    useEffect(() => {
        const onClickOutside = (e) => {
            if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggestions(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    // Debounced prefix search — only once Category + Selection Type are chosen
    useEffect(() => {
        if (!category || !selectionType || !searchText.trim()) {
            dispatch(clearItemSuggestions());
            return;
        }
        const t = setTimeout(() => {
            dispatch(fetchItemSuggestions({ prefix: searchText.trim(), type: selectionType, category }));
            setShowSuggestions(true);
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, selectionType, category]);

    // Prefill the details form once ViewItemCodeUpdation resolves
    useEffect(() => {
        if (!details) return;
        setItemName(details.Itemname || '');
        setBasicPrice(details.Basicprice || '');
        setSpecification(details.Specification || '');
        setUnits('');   // legacy leaves the Units dropdown unselected even though a current value exists
        setHsnId('');   // same for HSN — the readonly text shows the current code, the dropdown must be re-picked
        setRemarks('');
    }, [details]);

    useEffect(() => {
        if (saveResult === 'Successfull') {
            showToast('success', 'Item code update submitted successfully.');
            dispatch(clearSaveResult());
            handleReset();
        }
        if (saveError) {
            const msg = typeof saveError === 'string' ? saveError : 'Failed to save item code update.';
            setFormError(msg);
            showToast('error', msg);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult, saveError, dispatch]);

    const handleCategoryChange = (e) => {
        setFormError('');
        setCategory(e.target.value);
        setSelectionType('');
        setSearchText('');
        dispatch(clearItemSuggestions());
    };

    const handleSelectionTypeChange = (e) => {
        if (!category) { showToast('warning', 'Select Search By Category'); return; }
        setFormError('');
        setSelectionType(e.target.value);
        setSearchText('');
        dispatch(clearItemSuggestions());
    };

    const handlePickSuggestion = (s) => {
        setSearchText(s.Search || '');
        setShowSuggestions(false);
    };

    const handleAdd = () => {
        if (!category)      { showToast('error', 'Select Search By Category'); return; }
        if (!selectionType) { showToast('error', 'Select Search By Type'); return; }
        if (!searchText.trim()) { showToast('error', 'Enter an item to search for'); return; }
        setFormError('');
        const itemCode = searchText.split(',')[0].trim();
        dispatch(fetchItemForUpdate(itemCode));
        setShowSuggestions(false);
    };

    const handleClearSearch = () => {
        setSearchText('');
        dispatch(clearItemSuggestions());
        dispatch(clearItemForUpdate());
        setFormError('');
    };

    const handleReset = () => {
        setCategory('');
        setSelectionType('');
        setSearchText('');
        setItemName(''); setBasicPrice(''); setHsnId(''); setSpecification(''); setUnits(''); setRemarks('');
        setFormError('');
        dispatch(clearItemSuggestions());
        dispatch(clearItemForUpdate());
    };

    const handleSubmit = async () => {
        if (!itemName.trim())        { setFormError('Please Enter Item Name'); return; }
        if (!basicPrice || parseFloat(basicPrice) <= 0) { setFormError('Invalid Basic Price'); return; }
        if (!hsnId)                  { setFormError('Select HSN'); return; }
        if (!specification.trim())   { setFormError('Please Enter Specification'); return; }
        if (!units)                  { setFormError('Select Units'); return; }
        if (!remarks.trim())         { setFormError('Please Enter Remarks'); return; }
        setFormError('');

        const itemCode = details?.ItemCode;
        const pendingCheck = await dispatch(checkItemCodePendings(itemCode));
        if (!checkItemCodePendings.fulfilled.match(pendingCheck) || pendingCheck.payload !== 'Successfull') {
            setFormError(pendingCheck.payload || 'This item already has a pending update');
            return;
        }

        dispatch(submitItemCodeUpdate({
            UItemCode:      itemCode,
            UItemname:      itemName.trim().toUpperCase(),
            UBasicprice:    basicPrice,
            UHSNCode:       hsnId,
            USpecification: specification.trim(),
            UUnits:         units,
            UDate:          todayFormatted(),
            URemarks:       remarks.trim(),
            UCreatedby:     currentUser,
            URoleID:        String(roleId),
        }));
    };

    const hasConnectedInfo = !!details?.ConnectedTanNo;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-4xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <PackageSearch className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Master Data
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">ItemCode Updation</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                Search an existing item and submit updated details for approval.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">

                {/* Search */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                        <Search className="h-4 w-4 text-indigo-500" />
                        <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Find Item</h2>
                    </div>

                    <div className="p-4 md:p-5">
                        <div className="grid grid-cols-1 md:grid-cols-[repeat(2,minmax(0,1fr))_2fr] gap-4 items-end">
                            <div>
                                <Label required>Item Category</Label>
                                <div className="relative">
                                    <select value={category} onChange={handleCategoryChange} disabled={categoryLoading} className={inputCls}>
                                        <option value="">-Please Select-</option>
                                        {categoryList.map((c) => (
                                            <option key={c.Categoryid} value={c.Categoryid}>{c.CategoryValue}</option>
                                        ))}
                                    </select>
                                    {categoryLoading && (
                                        <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label required>Search By</Label>
                                <select value={selectionType} onChange={handleSelectionTypeChange} disabled={!category} className={inputCls}>
                                    <option value="">-Please Select-</option>
                                    {SELECTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>

                            <div className="relative" ref={searchBoxRef}>
                                <Label required>Item Search</Label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        disabled={!selectionType}
                                        placeholder="Start typing to search…"
                                        className={`${inputCls} uppercase`}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value.toUpperCase())}
                                        onFocus={() => searchText && setShowSuggestions(true)}
                                    />
                                    {suggestionsLoading && (
                                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-indigo-500 animate-spin" />
                                    )}
                                </div>

                                {showSuggestions && suggestions.length > 0 && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
                                        {suggestions.map((s, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => handlePickSuggestion(s)}
                                                className="block w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-b border-gray-50 dark:border-gray-700 last:border-0"
                                            >
                                                {s.Search}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 mt-4">
                            <button
                                onClick={handleAdd}
                                disabled={detailsLoading}
                                className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {detailsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                                Add
                            </button>
                            <button
                                onClick={handleClearSearch}
                                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                <X className="h-3.5 w-3.5" />
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Details / update form */}
                {details && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                            <PackageSearch className="h-4 w-4 text-indigo-500" />
                            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">Item Category — {details.ItemCode}</h2>
                        </div>

                        <div className="p-4 md:p-5 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label>Item Code</Label>
                                    <input className={inputCls} value={details.ItemCode || ''} readOnly disabled />
                                </div>
                                <div>
                                    <Label required>Item Name</Label>
                                    <input className={`${inputCls} uppercase`} value={itemName}
                                        onChange={(e) => { setFormError(''); setItemName(e.target.value.toUpperCase()); }} />
                                </div>
                                <div>
                                    <Label required>Basic Price</Label>
                                    <input type="text" inputMode="decimal" className={inputCls} value={basicPrice}
                                        onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) { setFormError(''); setBasicPrice(e.target.value); } }} />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <Label>Current HSN Code</Label>
                                    <input className={inputCls} value={details.HSNCode || ''} readOnly disabled />
                                </div>
                                <div className="md:col-span-2">
                                    <Label required>HSN Code</Label>
                                    <select value={hsnId} onChange={(e) => { setFormError(''); setHsnId(e.target.value); }} className={inputCls}>
                                        <option value="">-Please Select-</option>
                                        {hsnList.map((h) => (
                                            <option key={h.HSNID} value={h.HSNID}>{h.HSNCode}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {hasConnectedInfo && (
                                <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/10 p-3">
                                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1.5">
                                        <Link2 className="h-3.5 w-3.5" /> Connected Item
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Item Code: </span>
                                            <span className="font-semibold text-gray-800 dark:text-gray-100">{details.ConnectedItemcode}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">DCA: </span>
                                            <span className="font-semibold text-gray-800 dark:text-gray-100">{details.ConnectedDCA}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">SDCA: </span>
                                            <span className="font-semibold text-gray-800 dark:text-gray-100">{details.ConnectedSDca}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Specification</p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <Label required>Specification</Label>
                                        <input className={inputCls} value={specification}
                                            onChange={(e) => { setFormError(''); setSpecification(e.target.value); }} />
                                    </div>
                                    <div>
                                        <Label required>Units</Label>
                                        <select value={units} onChange={(e) => { setFormError(''); setUnits(e.target.value); }} className={inputCls}>
                                            <option value="">-Please Select-</option>
                                            {unitsList.map((u) => (
                                                <option key={u.Unitsval} value={u.Unitsval}>{u.Unitstext}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                <div>
                                    <Label>Date</Label>
                                    <input className={inputCls} value={todayFormatted()} readOnly disabled />
                                </div>
                                <div className="md:col-span-2">
                                    <Label required>Remarks</Label>
                                    <textarea rows={2} className={inputCls} value={remarks}
                                        onChange={(e) => { setFormError(''); setRemarks(e.target.value); }} />
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSubmit}
                                    disabled={saveLoading || checkingPendings}
                                    className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {(saveLoading || checkingPendings) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                    Submit
                                </button>
                                <button
                                    onClick={handleReset}
                                    disabled={saveLoading}
                                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    Reset
                                </button>
                            </div>

                            {formError && (
                                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800/50 text-sm text-rose-600 dark:text-rose-400">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    {formError}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ItemCodeUpdation;
